import React, { useEffect, useState, useCallback, useRef } from "react";
import * as echarts from "echarts";
import ReactDatePicker from "react-datepicker";
import Loader from "react-spinner-loader";
import moment from "moment";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { useTranslation } from "react-i18next";
import ColdWaveBarChart from "./ColdWaveBarChart";
import ColdWaveDashboardcard from "./ColdWaveDashboardcard";
import { dashboardCardConfigForColdwave } from "./DashboardConfigForColdwave";
import { coldwavecardsdata } from "../../../../Services/EndPoints";
import { getData } from "../../../../Services/ProfileServices";
import "react-datepicker/dist/react-datepicker.css";
import "./ColdwaveDashboard.css";

// ── Collect all card keys + labels from all groups ───────────────────────
const allCardItems = (dashboardCardConfigForColdwave || []).flatMap(g => g.data || []);

const LABEL_MAP = {
  number_of_deaths: "Loss of Life",
  number_of_bonfire_places: "Bonfire Places",
  number_of_night_shelters: "Night Shelters",
  number_of_ppl_taking_shelter_in_night_shelters: "People in Shelters",
  number_of_blankets_distributed: "Blankets Distributed",
};
const fmtLabel = (key) => LABEL_MAP[key] || (key || "").split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const BLUES = ["#1C4E72", "#2A6591", "#3A85B5", "#4BA3D3", "#5AA0C0", "#6DBDD6", "#85CEE0", "#A0DDEB"];

// ── Polar Rose Chart ─────────────────────────────────────────────────────
function PolarRoseChart({ cardData }) {
  const ref = useRef(null);
  const inst = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const items = allCardItems.filter(c => (cardData[c.key] || 0) > 0);
    const total = items.reduce((s, c) => s + (cardData[c.key] || 0), 0);

    const option = {
      title: { text: "IMPACT ROSE", left: "center", top: 14, textStyle: { color: "#0F2D42", fontSize: 12, fontWeight: "700", fontFamily: "'Sora',sans-serif" } },
      tooltip: {
        trigger: "item",
        confine: true,
        formatter: p => `<div style="padding:4px"><div style="font-weight:700;color:#0F2D42;margin-bottom:4px">${p.name}</div><div style="color:#2A6591;font-weight:800;font-size:16px">${p.value.toLocaleString("en-IN")}</div><div style="color:#7A9BB0;font-size:11px">${((p.value / (total || 1)) * 100).toFixed(1)}%</div></div>`,
        backgroundColor: "#fff", borderColor: "rgba(42,101,145,.1)", borderWidth: 1,
        extraCssText: "border-radius:12px;box-shadow:0 4px 16px rgba(15,45,66,.1);",
      },
      legend: { 
        orient: "horizontal", bottom: 10, left: "center", width: "80%",
        itemWidth: 8, itemHeight: 8, textStyle: { color: "#7A9BB0", fontSize: 8, fontWeight: "600" }
      },
      series: [{
        type: "pie",
        roseType: "area",
        radius: ["12%", "62%"],
        center: ["50%", "46%"],
        padding: [0, 0, 20, 0],
        data: items.map((c, i) => ({
          value: cardData[c.key] || 0,
          name: fmtLabel(c.key),
          itemStyle: { color: BLUES[i % BLUES.length], borderRadius: 6 },
        })),
        label: { show: false },
        emphasis: { itemStyle: { shadowBlur: 12, shadowColor: "rgba(0,0,0,.12)" } },
      }],
    };

    if (inst.current) inst.current.dispose();
    inst.current = echarts.init(ref.current);
    inst.current.setOption(option);
    const resize = () => inst.current?.resize();
    window.addEventListener("resize", resize);
    return () => { window.removeEventListener("resize", resize); inst.current?.dispose(); };
  }, [cardData]);

  return <div className="cwd-chart-cell"><div ref={ref} style={{ width: "100%", height: "100%" }} /></div>;
}

// ── Radar Chart ──────────────────────────────────────────────────────────
function RadarChart({ cardData }) {
  const ref = useRef(null);
  const inst = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const items = allCardItems.filter(c => (cardData[c.key] || 0) >= 0);
    const maxVal = Math.max(...items.map(c => cardData[c.key] || 0), 1);

    const option = {
      title: { text: "IMPACT RADAR", left: "center", top: 14, textStyle: { color: "#0F2D42", fontSize: 12, fontWeight: "700", fontFamily: "'Sora',sans-serif" } },
      tooltip: {
        trigger: "item",
        confine: true,
        formatter: p => {
          const vals = p.value.map((v, i) => `<span style="color:#2A6591;font-weight:700">${items[i] ? fmtLabel(items[i].key) : ""}</span>: ${v}`).join("<br/>");
          return `<div style="padding:4px;font-size:12px">${vals}</div>`;
        },
        backgroundColor: "#fff", borderColor: "rgba(42,101,145,.1)", borderWidth: 1,
      },
      legend: { 
        orient: "horizontal", bottom: 10, left: "center", width: "70%",
        itemWidth: 8, itemHeight: 8, textStyle: { color: "#7A9BB0", fontSize: 8, fontWeight: "600" }
      },
      radar: {
        indicator: items.map(c => {
          const label = fmtLabel(c.key);
          return { 
            name: label.includes(" ") ? label.replace(" ", "\n") : label,
            max: maxVal * 1.2 
          };
        }),
        center: ["50%", "48%"], radius: "48%",
        shape: "polygon",
        axisName: { color: "#3A5F78", fontSize: 9, fontWeight: 600, lineHeight: 12 },
        splitArea: { areaStyle: { color: ["rgba(42,101,145,.02)", "rgba(42,101,145,.05)"] } },
        splitLine: { lineStyle: { color: "rgba(42,101,145,.1)" } },
        axisLine: { lineStyle: { color: "rgba(42,101,145,.12)" } },
      },
      series: [{
        type: "radar",
        data: [{
          value: items.map(c => cardData[c.key] || 0),
          name: "Impact",
          areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: "#2A659140" }, { offset: 1, color: "#4BA3D320" }]) },
          lineStyle: { color: "#2A6591", width: 2 },
          itemStyle: { color: "#2A6591", borderColor: "#fff", borderWidth: 2 },
          symbol: "circle", symbolSize: 7,
        }],
      }],
    };

    if (inst.current) inst.current.dispose();
    inst.current = echarts.init(ref.current);
    inst.current.setOption(option);
    const resize = () => inst.current?.resize();
    window.addEventListener("resize", resize);
    return () => { window.removeEventListener("resize", resize); inst.current?.dispose(); };
  }, [cardData]);

  return <div className="cwd-chart-cell"><div ref={ref} style={{ width: "100%", height: "100%" }} /></div>;
}

// ── Funnel Chart ─────────────────────────────────────────────────────────
function FunnelChart({ cardData }) {
  const ref = useRef(null);
  const inst = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const items = allCardItems
      .map(c => ({ name: fmtLabel(c.key), value: cardData[c.key] || 0 }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value);

    const option = {
      title: { text: "IMPACT FUNNEL", left: "center", top: 14, textStyle: { color: "#0F2D42", fontSize: 12, fontWeight: "700", fontFamily: "'Sora',sans-serif" } },
      tooltip: {
        trigger: "item",
        confine: true,
        formatter: p => `<div style="padding:4px"><div style="font-weight:700;color:#0F2D42">${p.name}</div><div style="color:#2A6591;font-weight:800;font-size:16px">${p.value.toLocaleString("en-IN")}</div></div>`,
        backgroundColor: "#fff", borderColor: "rgba(42,101,145,.1)", borderWidth: 1,
        extraCssText: "border-radius:12px;box-shadow:0 4px 16px rgba(15,45,66,.1);",
      },
      legend: { orient: "horizontal", bottom: 10, itemWidth: 10, itemHeight: 10, textStyle: { color: "#7A9BB0", fontSize: 9, fontWeight: "600" } },
      series: [{
        type: "funnel",
        left: "10%", right: "10%", top: 60, bottom: 65,
        width: "80%",
        gap: 4,
        sort: "descending",
        label: { show: true, position: "inside", formatter: "{c}", fontSize: 11, fontWeight: 700, color: "#fff" },
        labelLine: { show: false },
        itemStyle: { borderColor: "#fff", borderWidth: 2 },
        emphasis: { label: { fontSize: 13, fontWeight: 800 }, itemStyle: { shadowBlur: 12, shadowColor: "rgba(0,0,0,.12)" } },
        data: items.map((d, i) => ({
          ...d,
          itemStyle: { color: BLUES[i % BLUES.length] },
        })),
      }],
    };

    if (inst.current) inst.current.dispose();
    inst.current = echarts.init(ref.current);
    inst.current.setOption(option);
    const resize = () => inst.current?.resize();
    window.addEventListener("resize", resize);
    return () => { window.removeEventListener("resize", resize); inst.current?.dispose(); };
  }, [cardData]);

  return <div className="cwd-chart-cell"><div ref={ref} style={{ width: "100%", height: "100%" }} /></div>;
}

// ── Main Dashboard ───────────────────────────────────────────────────────
export default function ColdWaveDashboard() {
  const { t } = useTranslation();
  const handle = useFullScreenHandle();

  const [loader, setLoader] = useState(false);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState("");
  const [cardData, setCardData] = useState({});
  const [internalFromDate, setInternalFromDate] = useState(new Date());
  const [internalToDate, setInternalToDate] = useState(new Date());
  const [appliedFromDate, setAppliedFromDate] = useState(new Date());
  const [appliedToDate, setAppliedToDate] = useState(new Date());
  const [isTrendExpanded, setIsTrendExpanded] = useState(false);
  const [isTrendMinimized, setIsTrendMinimized] = useState(false);
  const [selectedYear, setSelectedYear] = useState(2026);

  const startDateStr = appliedFromDate ? moment(appliedFromDate).format("YYYY-MM-DD") : "";
  const endDateStr = appliedToDate ? moment(appliedToDate).format("YYYY-MM-DD") : "";

  // Fetch aggregate card data
  const fetchCardData = useCallback(async (from, to) => {
    if (!from || !to) return;
    setLoader(true);
    try {
      const res = await getData(
        coldwavecardsdata.replace("FROMDATE", moment(from).format("YYYY-MM-DD")).replace("TODATE", moment(to).format("YYYY-MM-DD"))
      );
      setCardData(res?.[0] || {});
    } catch { setError("Failed to load data."); }
    finally { setLoader(false); }
  }, []);

  useEffect(() => { fetchCardData(appliedFromDate, appliedToDate); }, []); // eslint-disable-line

  useEffect(() => {
    if (internalFromDate && internalToDate && internalFromDate > internalToDate)
      setError("From date must be before To date.");
    else if (!internalFromDate || !internalToDate) setError(null);
    else setError(null);
  }, [internalFromDate, internalToDate]);

  const toggleFullscreen = () => { isFullscreen ? handle.exit() : handle.enter(); setIsFullscreen(!isFullscreen); };

  const handleApply = () => {
    if (!internalFromDate || !internalToDate) { setError("Please select both From and To dates"); return; }
    if (internalFromDate > internalToDate) { setError("From date cannot be greater than To date"); return; }
    setError(null);
    setAppliedFromDate(internalFromDate);
    setAppliedToDate(internalToDate);
    fetchCardData(internalFromDate, internalToDate);
  };

  const handleYearChange = (e) => {
    const year = parseInt(e.target.value);
    setSelectedYear(year);
    const today = new Date();
    const newFrom = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31);
    const nextTo = today <= yearEnd ? today : yearEnd;
    setInternalFromDate(newFrom);
    setInternalToDate(nextTo);
    setAppliedFromDate(newFrom);
    setAppliedToDate(nextTo);
    fetchCardData(newFrom, nextTo);
  };
  const ReadOnlyDateInput = React.forwardRef((props, ref) => (
  <input {...props} ref={ref} onKeyDown={e => e.preventDefault()} readOnly />
));

  const isApplyDisabled = !!error || !internalFromDate || !internalToDate;

  const formatIncidentLabel = (key) => {
    if (!key) return "";
    return LABEL_MAP[key] || key.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  };

  return (
    <FullScreen handle={handle}>
      <div className={`cwd-root${isFullscreen ? " cwd-fs" : ""}`}>
        <Loader show={loader} type="body" stack="vertical" message="Loading Data" />

        {/* ── NAV ── */}
        <nav className="cwd-nav">
          <div className="cwd-nav-brand">
            <div className="cwd-nav-logo">
              <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
                <circle cx="10" cy="10" r="2.8" fill="white" opacity=".9"/>
                <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.34 4.34l1.42 1.42M14.24 14.24l1.42 1.42M4.34 15.66l1.42-1.42M14.24 5.76l1.42-1.42"
                  stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="cwd-nav-title">Cold Wave Dashboard</div>
          </div>

          

          <div className="cwd-nav-right">
            <div className="cwd-nav-group">
              <span className="cwd-nav-label">Year</span>
              <select className="cwd-nav-select" value={selectedYear} onChange={handleYearChange}>
                <option value={2024}>2024</option>
                <option value={2025}>2025</option>
                <option value={2026}>2026</option>
              </select>
            </div>

            <div className="cwd-daterange">
              <div className="cwd-daterange-field">
                <span className="cwd-dr-label">{t("flooddisaster.datetxtfrom")}</span>
                <ReactDatePicker selected={internalFromDate} onChange={(date) => setInternalFromDate(date)}
                  dateFormat="dd/MM/yyyy" isClearable placeholderText="Select date" todayButton="Today" className="cwd-dp-input"
                  customInput={<ReadOnlyDateInput />} />
              </div>
              <div className="cwd-dr-divider" />
              <div className="cwd-daterange-field">
                <span className="cwd-dr-label">{t("flooddisaster.datetxtto")}</span>
                <ReactDatePicker selected={internalToDate} onChange={(date) => setInternalToDate(date)}
                  dateFormat="dd/MM/yyyy" isClearable placeholderText="Select date" todayButton="Today" className="cwd-dp-input" customInput={<ReadOnlyDateInput />} />
              </div>
            </div>
            <button className="cwd-btn-apply" onClick={handleApply} disabled={isApplyDisabled}>Apply</button>
            <button className="cwd-btn-icon" onClick={toggleFullscreen}>
              {isFullscreen ? (
                <svg viewBox="0 0 18 18" fill="none" width="14" height="14"><path d="M2 6H6V2M16 6H12V2M2 12H6V16M16 12H12V16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
              ) : (
                <svg viewBox="0 0 18 18" fill="none" width="14" height="14"><path d="M2 6V2h4M12 2h4v4M2 12v4h4M16 12v4h-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
              )}
            </button>
          </div>
        </nav>

        {error && (
          <div className="cwd-error-strip">
            <svg viewBox="0 0 16 16" fill="none" width="13" height="13">
              <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4"/>
              <path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            {error}
          </div>
        )}

        <div className="cwd-content">
          {/* ── HORIZONTAL CARDS (top) ── */}
          {dashboardCardConfigForColdwave?.map((card, i) => (
            <ColdWaveDashboardcard
              key={i} config={card}
              cardData={cardData}
              onIncidentSelect={(key) => setSelectedIncident(key)}
              selectedIncident={selectedIncident}
              startDateStr={startDateStr} endDateStr={endDateStr}
            />
          ))}

          {/* ── BAR CHART ── */}
          <div className={`cwd-chart-wrap${isTrendExpanded ? " cwd-chart-wrap--expanded" : ""}`}>
            <div className="cwd-chart-top">
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div>
                  <h3 className="cwd-chart-heading">
                    {selectedIncident ? formatIncidentLabel(selectedIncident) : "Incident Trend Overview"}
                  </h3>
                  {!selectedIncident && <p className="cwd-chart-range">Select any incident from the cards above to view its trend</p>}
                  {selectedIncident && <p className="cwd-chart-range">{moment(startDateStr).format("DD-MM-YYYY")} — {moment(endDateStr).format("DD-MM-YYYY")}</p>}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                {selectedIncident && (
                  <button className="cwd-badge-btn" onClick={() => setSelectedIncident("")}>
                    {formatIncidentLabel(selectedIncident)}
                    <svg viewBox="0 0 10 10" fill="none" width="9" height="9"><path d="M2 2l6 6M8 2L2 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                  </button>
                )}
                {!isTrendExpanded && (
                  <button 
                    className="cwd-btn-icon" 
                    style={{ background: "rgba(42,101,145,.08)", border: "1px solid rgba(42,101,145,.12)", color: "var(--primary)" }}
                    onClick={() => setIsTrendMinimized(!isTrendMinimized)}
                    title={isTrendMinimized ? "Expand Card" : "Collapse Card"}
                  >
                    {isTrendMinimized ? (
                      <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M4 8h8M8 4v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
                    ) : (
                      <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M4 8h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
                    )}
                  </button>
                )}
                <button 
                  className="cwd-btn-icon" 
                  style={{ background: "rgba(42,101,145,.08)", border: "1px solid rgba(42,101,145,.12)", color: "var(--primary)" }}
                  onClick={() => setIsTrendExpanded(!isTrendExpanded)}
                  title={isTrendExpanded ? "Collapse Fullscreen" : "Expand Fullscreen"}
                >
                  {isTrendExpanded ? (
                    <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M13 7H9V3M9 7L15 1M7 13V9H3M7 9L1 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  ) : (
                    <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><path d="M10 1H15V6M15 1L9 7M6 15H1V10M1 15L7 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  )}
                </button>
              </div>
            </div>
            {(isTrendExpanded || !isTrendMinimized) && (
              <>
                {!selectedIncident && (
                  <div className="cwd-chart-empty">
                <div className="cwd-chart-empty-icon">
                  <svg viewBox="0 0 48 48" fill="none" width="40" height="40">
                    <rect x="4" y="30" width="8" height="14" rx="2" fill="#C5DFF0"/><rect x="16" y="20" width="8" height="24" rx="2" fill="#A0C8E0"/>
                    <rect x="28" y="12" width="8" height="32" rx="2" fill="#7AB4D0"/><rect x="40" y="22" width="8" height="22" rx="2" fill="#5AA0C0"/>
                    <path d="M4 8h40" stroke="#C5DFF0" strokeWidth="1.5" strokeDasharray="3 3"/>
                  </svg>
                </div>
                <p className="cwd-chart-empty-text">Click any incident card above to visualize its trend</p>
              </div>
            )}
                <div className="cwd-chart-area">
                  <ColdWaveBarChart fromDate={appliedFromDate} toDate={appliedToDate}
                    startDateStr={startDateStr} endDateStr={endDateStr}
                    selectedIncident={selectedIncident} title={selectedIncident}
                    isTrendExpanded={isTrendExpanded}
                  />
                </div>
              </>
            )}
          </div>

          {/* ── 3 EXTRA CHARTS ── */}
          <div className="cwd-bottom-charts">
            <PolarRoseChart cardData={cardData} />
            <RadarChart cardData={cardData} />
            <FunnelChart cardData={cardData} />
          </div>
        </div>
      </div>
    </FullScreen>
  );
}